/**
 * PCM vs CCM enrollment rules:
 * - PCM: one high-risk chronic condition (CPT 99424–99427)
 * - CCM: two or more chronic conditions (CPT 99490…)
 * A patient cannot be in both at the same time.
 */

export function countChronicConditions(conditions) {
  if (!Array.isArray(conditions)) return 0;
  return conditions.filter(Boolean).length;
}

export function practiceHasProgram(programs, shortName) {
  const want = String(shortName || '').trim().toLowerCase();
  if (!want) return false;
  return (Array.isArray(programs) ? programs : []).some((prog) => {
    const key = String(
      typeof prog === 'string' ? prog : (prog?.short_name || prog?.id || prog?.name || '')
    )
      .trim()
      .toLowerCase();
    return key === want;
  });
}

/**
 * @returns {'pcm'|'ccm'|null}
 */
export function resolveCareManagementProgram({
  conditionCount,
  practiceHasPcm,
  practiceHasCcm
}) {
  const count = Number(conditionCount) || 0;
  if (count >= 2 && practiceHasCcm) return 'ccm';
  if (count === 1 && practiceHasPcm) return 'pcm';
  return null;
}

export function applyCareManagementSelection(selectedPrograms, recommended) {
  const rest = (Array.isArray(selectedPrograms) ? selectedPrograms : []).filter(
    (key) => key !== 'ccm' && key !== 'pcm'
  );
  if (recommended === 'pcm' || recommended === 'ccm') {
    return [...rest, recommended];
  }
  return rest;
}

export function pcmCcmToggleConflictMessage(programKey, conditionCount, { practiceHasPcm, practiceHasCcm }) {
  const key = String(programKey || '').trim().toLowerCase();
  const count = Number(conditionCount) || 0;
  if (key === 'pcm') {
    if (count >= 2) {
      return practiceHasCcm
        ? 'This patient has two or more chronic conditions. Enroll in CCM instead of PCM.'
        : 'PCM is for a single high-risk chronic condition. This patient has two or more conditions.';
    }
    if (count < 1) {
      return 'PCM requires one high-risk chronic condition expected to last at least 3 months.';
    }
    return null;
  }
  if (key === 'ccm') {
    if (count === 1) {
      return practiceHasPcm
        ? 'This patient has one chronic condition. Enroll in PCM instead of CCM.'
        : 'CCM requires at least two chronic conditions.';
    }
    if (count < 2) {
      return 'CCM requires at least two chronic disease conditions.';
    }
    return null;
  }
  return null;
}
